import axios from 'axios'
import { publicPath } from '@src/../vue.config'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { TIPOS_EVALUACION_ANUAL } from '../TIPOS_EVALUACION_ANUAL'

/**
 * Clase para generar el archivo XLSX de una evaluación anual (administrativa u operativa)
 * Encapsula toda la lógica de transformación y escritura en la plantilla.
 */
export default class ExportEvaluacionAnual {
  worksheet
  buffer

  /**
   * @param {Object} evaluacion - Objeto de evaluación anual.
   * @param {Function} getFullNameFn - Función para obtener nombre completo (inyectada desde mixin global).
   */
  constructor(evaluacion, getFullNameFn) {
    this.evaluacion = evaluacion
    this.getFullName = getFullNameFn || ((p) => `${p?.nombre || ''}`)
  }

  getTemplateFileName() {
    if (!this.evaluacion) return ''
    return this.evaluacion.fk_idCatTipoEvaluacion == TIPOS_EVALUACION_ANUAL.EVACOM
      ? 'evaluacion_anual_competencias_evacom.xlsx'
      : 'evaluacion_anual_competencias_evacom_mandos.xlsx'
  }

  async getFileTemplate() {
    try {
      const file = await axios(`${publicPath}${this.getTemplateFileName()}`, { responseType: 'arraybuffer' })
      return file.data
    } catch (error) {
      console.error('Error al obtener la plantilla de evaluación anual:', error)
      throw new Error('No se pudo obtener la plantilla de evaluación anual.')
    }
  }

  writeCell(cellPosition, value) {
    const cell = this.worksheet.getCell(cellPosition)
    cell.value = value ?? ''
  }

  writeEvaluadorData() {
    const ev = this.evaluacion.PersonalEvaluador
    if (!ev) return
    this.writeCell('B8', this.getFullName(ev))
    this.writeCell('B9', ev.CatPuestoDependencia?.catPuesto?.puesto)
    this.writeCell('B10', ev.CatPuestoDependencia?.catDependencia?.nombre)
    this.writeCell('D10', ev.CatArea ? ev.CatArea.nombre : '-')
    this.writeCell('B12', ev.Contacto?.telefonoCelular)
    this.writeCell('B13', ev.Contacto?.correoElectronico)
  }

  writeEvaluadoData() {
    const personal = this.evaluacion.Personal
    if (!personal) return
    const nombreCompleto = this.getFullName(personal)

    if (this.evaluacion.fk_idCatTipoEvaluacion === TIPOS_EVALUACION_ANUAL.EVACOM) {
      this.writeCell('C8', nombreCompleto)
      this.writeCell('L8', personal.CatPuestoDependencia?.catPuesto?.puesto)
      this.writeCell('R8', this.evaluacion.fecha)
      this.writeCell('C10', personal.CatTipoFuncion?.title)
      this.writeCell('L10', personal.CatArea?.nombre)
      this.writeCell('C11', personal.unidadAnalisisTactico)
      this.writeCell('B12', personal.CUIP)
      this.writeCell('M12', 'SECRETARÍA DE SEGURIDAD CIUDADANA')
      this.writeCell('B14', personal.Contacto?.telefonoCelular)
      this.writeCell('N14', personal.Contacto?.correoElectronico)
    } else {
      this.writeCell('K8', nombreCompleto)
      this.writeCell('R8', personal.CatPuestoDependencia?.catPuesto?.puesto)
      this.writeCell('K10', personal.CatTipoFuncion?.title)
      this.writeCell('M11', personal.unidadAnalisisTactico)
      this.writeCell('Q10', personal.CatArea?.nombre)
      this.writeCell('K12', personal.CUIP)
      this.writeCell('J14', personal.Contacto?.telefonoCelular)
      this.writeCell('P14', personal.Contacto?.correoElectronico)
      this.writeCell('Q12', `Fecha de aplicación de evaluación: ${this.evaluacion.fecha}`)
    }
  }

  writeCumplimientoFunciones() {
    let idx = 23
    this.evaluacion.cumplimientoFunciones.forEach((reactivo) => {
      const valor = Number(reactivo.calificacion)
      this.writeCell('R' + idx, valor)
      idx++
    })
  }

  /**
   * Calcula el puntaje de cada rubro sumando las calificaciones de los reactivos correspondientes
   * @returns {Object} Objeto con los puntajes por rubro
   */
  calcularPuntajesPorRubro() {
    const puntajes = {}

    this.evaluacion.cumplimientoFunciones.forEach((reactivo) => {
      const rubro = reactivo.DescripcionCriterio?.CatCriterioEvaluacion?.title
      if (!rubro) return

      const calificacion = Number(reactivo.calificacion) || 0

      if (!puntajes[rubro]) {
        puntajes[rubro] = 0
      }
      puntajes[rubro] += calificacion
    })

    return puntajes
  }

  /**
   * Determina el resultado según el puntaje
   * @param {number} puntaje - Puntaje calculado del rubro
   * @returns {string} Resultado: 'Excelente', 'Satisfactorio' o 'Insatisfactorio'
   */
  determinarResultado(puntaje) {
    if (this.evaluacion.fk_idCatTipoEvaluacion === TIPOS_EVALUACION_ANUAL.EVACOM) {
      if (puntaje >= 31.5 && puntaje <= 35) {
        return 'Excelente'
      } else if (puntaje >= 24.5 && puntaje <= 31.4) {
        return 'Satisfactorio'
      } else if (puntaje >= 7 && puntaje <= 24.4) {
        return 'Insatisfactorio'
      }
      return ''
    } else {
      if (puntaje >= 22.5 && puntaje <= 25) {
        return 'Excelente'
      } else if (puntaje >= 17.5 && puntaje <= 22.4) {
        return 'Satisfactorio'
      } else if (puntaje >= 5 && puntaje <= 17.4) {
        return 'Insatisfactorio'
      }
      return ''
    }
  }

  /**
   * Escribe los resultados de cada rubro en las celdas correspondientes
   */
  writeResultadosRubros() {
    const puntajes = this.calcularPuntajesPorRubro()

    const rubrosMapping = {
      'COMUNICACIÓN ASERTIVA':
        this.evaluacion.fk_idCatTipoEvaluacion === TIPOS_EVALUACION_ANUAL.EVACOM ? ['R79', 'R88'] : ['R64', 'R76'],
      'TOMA DE DECISIONES Y RESOLUCION DE CONFLICTOS':
        this.evaluacion.fk_idCatTipoEvaluacion === TIPOS_EVALUACION_ANUAL.EVACOM ? ['R80', 'R89'] : ['R65', 'R77'],
      'HABILIDADES DEL PENSAMIENTO':
        this.evaluacion.fk_idCatTipoEvaluacion === TIPOS_EVALUACION_ANUAL.EVACOM ? ['R81', 'R90'] : ['R66', 'R78'],
      'ÉTICA Y VALORES':
        this.evaluacion.fk_idCatTipoEvaluacion === TIPOS_EVALUACION_ANUAL.EVACOM ? ['R82', 'R91'] : ['R67', 'R79'],
      LIDERAZGO:
        this.evaluacion.fk_idCatTipoEvaluacion === TIPOS_EVALUACION_ANUAL.EVACOM ? ['R83', 'R92'] : ['R68', 'R80'],
      'INTELIGENCIA EMOCIONAL':
        this.evaluacion.fk_idCatTipoEvaluacion === TIPOS_EVALUACION_ANUAL.EVACOM ? ['R84', 'R93'] : ['R69', 'R81'],
      'CUMPLIMIENTO SOCIAL Y DEL DESEMPEÑO':
        this.evaluacion.fk_idCatTipoEvaluacion === TIPOS_EVALUACION_ANUAL.EVACOM ? ['R85', 'R94'] : ['R70', 'R82'],
    }

    Object.entries(rubrosMapping).forEach(([rubro, celdas]) => {
      const puntaje = puntajes[rubro] || 0
      const resultado = this.determinarResultado(puntaje)
      celdas.forEach((celda) => {
        this.writeCell(celda, resultado)
      })
    })
  }

  download() {
    saveAs(
      new Blob([this.buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      this.getTemplateFileName()
    )
  }

  async generateXlsx() {
    try {
      const template = await this.getFileTemplate()
      const workbook = new ExcelJS.Workbook()
      await workbook.xlsx.load(template)
      this.worksheet = workbook.getWorksheet(1)

      if (this.evaluacion.fk_idCatTipoEvaluacion === TIPOS_EVALUACION_ANUAL.EVACOM_MANDOS) {
        this.writeEvaluadorData()
      }
      this.writeEvaluadoData()
      this.writeCumplimientoFunciones()
      this.writeResultadosRubros()

      this.worksheet.removeConditionalFormatting()
      this.buffer = await workbook.xlsx.writeBuffer()
      this.download()
    } catch (error) {
      console.error('Error al generar el archivo de evaluación anual:', error)
      throw new Error('No se pudo generar el archivo de evaluación anual.')
    }
  }
}
